/**
 * Request Password Reset Page
 *
 * Allows users to request a password reset email.
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { emailSchema, tenantCodeSchema } from '../../../lib/validators/common';
import type { PasswordResetRequest } from '../../../types/auth.types';
import { useRequestPasswordReset } from '../hooks/usePasswordReset';
import { getErrorMessage } from '../../../lib/utils/errors';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { FormField } from '../../../components/forms/FormField';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

const passwordResetRequestSchema = z.object({
  tenantCode: tenantCodeSchema,
  email: emailSchema,
});

export const RequestPasswordResetPage: React.FC = () => {
  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<PasswordResetRequest>({
    resolver: zodResolver(passwordResetRequestSchema),
    defaultValues: {
      tenantCode: localStorage.getItem('remembered_tenant_code') || '',
      email: '',
    },
  });

  const { mutate: requestReset, isPending } = useRequestPasswordReset({
    onSuccess: () => {
      setSubmittedEmail(watch('email'));
      setShowSuccess(true);
    },
    onError: (error) => {
      // For security, show generic success message even on error
      // This prevents user enumeration
      setSubmittedEmail(watch('email'));
      setShowSuccess(true);
    },
  });

  const onSubmit = (data: PasswordResetRequest) => {
    requestReset(data);
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-accent-100">
              <CheckCircleIcon className="h-10 w-10 text-accent-600" />
            </div>
            <h2 className="mt-6 text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
              Check Your Email
            </h2>
            <div className="mt-4 space-y-4">
              <p style={{ color: 'var(--color-text-secondary)' }}>
                If an account exists with the email address:
              </p>
              <p className="text-lg font-medium" style={{ color: 'var(--color-text-primary)' }}>
                {submittedEmail}
              </p>
              <p style={{ color: 'var(--color-text-secondary)' }}>
                We've sent you a password reset link. Please check your inbox (and spam folder).
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm text-blue-800">
                <p className="font-medium mb-2">Next steps:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Check your email for the reset link</li>
                  <li>Click the link in the email</li>
                  <li>Enter your new password</li>
                  <li>Sign in with your new password</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="text-center space-y-2">
            <Button
              variant="primary"
              onClick={() => navigate('/login')}
              fullWidth
            >
              Back to Login
            </Button>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Didn't receive the email?{' '}
              <button
                type="button"
                className="font-medium text-primary-600 hover:text-primary-500"
                onClick={() => setShowSuccess(false)}
              >
                Try again
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div>
          <h2 className="mt-6 text-center text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
            Reset your password
          </h2>
          <p className="mt-2 text-center text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Enter your email and we'll send you a reset link
          </p>
        </div>

        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <FormField
              label="Tenant Code"
              required
              error={errors.tenantCode?.message}
            >
              <Input
                {...register('tenantCode')}
                type="text"
                placeholder="e.g., acme-corp"
                error={errors.tenantCode?.message}
                disabled={isPending}
              />
            </FormField>

            <FormField
              label="Email"
              required
              error={errors.email?.message}
            >
              <Input
                {...register('email')}
                type="email"
                placeholder="you@example.com"
                error={errors.email?.message}
                disabled={isPending}
              />
            </FormField>
          </div>

          <div>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={isPending}
              disabled={isPending}
            >
              Send Reset Link
            </Button>
          </div>

          <div className="text-center text-sm">
            <Link
              to="/login"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              Back to login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestPasswordResetPage;
